import { isOneLineName } from './cardName'

const SOFT_HYPHEN = '­'
// Shorter first words always fit beside the heart. Keeping this ≥ 8 also keeps
// the e2e fixture names ("Charlie Synth") free of soft hyphens, so `hasText`
// matchers still match them.
const MIN_WORD_LENGTH = 8
const MIN_FRAGMENT = 3
const NO_SPLIT_DIGRAPHS = new Set(['ch', 'ck', 'gh', 'ph', 'sh', 'th', 'qu'])

const isVowel = (ch: string | undefined) => !!ch && /[aeiouyàáâäãåæèéêëìíîïòóôöõøùúûüÿ]/i.test(ch)

const isOnset = (pair: string) => /^[bcdfgkpt][rl]$/.test(pair)

function hyphenateWord(word: string): string {
  if (word.length < MIN_WORD_LENGTH || word.includes(SOFT_HYPHEN) || !/^\p{L}+$/u.test(word)) return word
  const breaks = new Set<number>()
  for (let i = 2; i < word.length; i++) {
    if (isVowel(word[i - 2]) && !isVowel(word[i - 1]) && !isVowel(word[i])) {
      const pair = (word[i - 1] + word[i]).toLowerCase()
      // "fr", "pl", "tr"… start the next syllable together (Super-cali-fragilistic).
      if (isOnset(pair) && isVowel(word[i + 1])) breaks.add(i - 1)
      else if (!NO_SPLIT_DIGRAPHS.has(pair)) breaks.add(i)
    }
    if (isVowel(word[i - 1]) && !isVowel(word[i]) && isVowel(word[i + 1])) breaks.add(i)
  }
  let out = ''
  for (let i = 0; i < word.length; i++) {
    if (breaks.has(i) && i >= MIN_FRAGMENT && i <= word.length - MIN_FRAGMENT) out += SOFT_HYPHEN
    out += word[i]
  }
  return out
}

/**
 * Soft hyphens (U+00AD) at syllable-like breaks in the text's first word, so a
 * card name too long to sit beside the floated heart splits there with a
 * visible hyphen instead of leaving the first line empty. Only the first word:
 * a later word that doesn't fit wraps whole to the next line, as before.
 * Render-time only — never persist the result or match/search against it.
 */
export function hyphenateFirstWord(text: string): string {
  const match = /^(\s*)(\S+)/.exec(text)
  if (!match) return text
  const [lead, space, word] = match
  return space + hyphenateWord(word) + text.slice(lead.length)
}

/**
 * Whether a card's name gets those soft hyphens. Not on one-line names: they're
 * cut off with an ellipsis anyway, and a hyphen there only hides letters.
 */
export function hyphenatesName(compact: 'xs' | 'sm' | 'md' | undefined, nameLines: number | undefined): boolean {
  return !isOneLineName(compact, nameLines)
}
